/**
 * Shared shipping ETA + business-day math. Consumed by
 * `DeliveryEstimator` (PDP) and the mobile checkout submit bar.
 *
 * Keep this file browser-safe — no server-only imports.
 */

export type EtaWindow = {
  minDays: number;
  maxDays: number;
};

/**
 * Keyed by BD division name as used in the checkout form
 * (see `BD_DIVISIONS` in `src/lib/bd-divisions.ts`).
 */
export const SHIPPING_ETA_BY_DIVISION: Record<string, EtaWindow> = {
  Dhaka: { minDays: 2, maxDays: 3 },
  Chattogram: { minDays: 3, maxDays: 5 },
  Sylhet: { minDays: 3, maxDays: 5 },
  Rajshahi: { minDays: 3, maxDays: 5 },
  Khulna: { minDays: 3, maxDays: 5 },
  Barishal: { minDays: 3, maxDays: 6 },
  Rangpur: { minDays: 3, maxDays: 6 },
  Mymensingh: { minDays: 3, maxDays: 5 },
};

const DEFAULT_ETA: EtaWindow = { minDays: 3, maxDays: 6 };

export function addBusinessDays(from: Date, days: number): Date {
  const d = new Date(from);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    // BD couriers pause on Friday.
    if (d.getDay() !== 5) added++;
  }
  return d;
}

export function fmtEtaDate(d: Date, lang: "en" | "bn"): string {
  return d.toLocaleDateString(lang === "bn" ? "bn-BD" : "en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

export function getEtaWindow(division?: string | null, district?: string | null): EtaWindow {
  const base = (division && SHIPPING_ETA_BY_DIVISION[division]) || DEFAULT_ETA;
  // Dhaka city gets a day faster on the upper bound.
  if (division === "Dhaka" && district === "Dhaka") {
    return { minDays: 1, maxDays: Math.max(base.minDays, base.maxDays - 1) };
  }
  return base;
}

export function estimateEta(
  division?: string | null,
  district?: string | null,
  from: Date = new Date(),
): { min: Date; max: Date; window: EtaWindow } {
  const w = getEtaWindow(division, district);
  return {
    min: addBusinessDays(from, w.minDays),
    max: addBusinessDays(from, w.maxDays),
    window: w,
  };
}

export function formatEtaRange(
  division?: string | null,
  district?: string | null,
  lang: "en" | "bn" = "en",
): string {
  const { min, max } = estimateEta(division, district);
  return `${fmtEtaDate(min, lang)} – ${fmtEtaDate(max, lang)}`;
}

/**
 * The one delivery statement used across the checkout sidebar, the mobile bar
 * and the final confirm dialog. Derived from the real ETA window (never a
 * hard-coded "2–3 days") and localized, with Bengali digits for the shipment
 * count.
 */
export function formatDeliveryLabel(opts: {
  division?: string | null;
  district?: string | null;
  shipments?: number;
  lang: "en" | "bn";
}): string {
  const { division, district, shipments = 1, lang } = opts;
  const range = formatEtaRange(division, district, lang);
  if (shipments > 1) {
    const count = lang === "bn" ? shipments.toLocaleString("bn-BD") : String(shipments);
    return lang === "bn"
      ? `${count}টি শিপমেন্ট · ${range}`
      : `${count} shipments · ${range}`;
  }
  const place = division?.trim();
  return place ? `${place} · ${range}` : range;
}
